import { useInfiniteQuery } from '@tanstack/react-query';
import {
  HttpStatusCode,
  type AxiosError,
  type AxiosRequestConfig,
} from 'axios';

import { SecurityRoleTypeEnum } from 'abi-compliance-common-types';
import { complianceApi } from '~/shared/services/api';
import { clearParams } from '~/shared/utils/clearParams';
import {
  adaptEmployeeResponse,
  APIEmployeeResponse,
} from '../adapters/employee-response.adapter';

export type GetEmployeesFilterParams = {
  employeeNameOrId?: string;
  excludeBands?: string[];
  role?: SecurityRoleTypeEnum;
};

export type GetEmployeesParams = GetEmployeesFilterParams & {
  page?: number;
  limit?: number;
  orderField?: string;
  orderValue?: 'asc' | 'desc';
};

const EMPLOYEES_PAGE_SIZE = 20;
const MAX_RETRY_COUNT = 3;

async function searchEmployees(
  { page, limit, ...params }: GetEmployeesParams,
  config: AxiosRequestConfig = {}
) {
  const clearedParams = clearParams(params);

  const requestParams = {
    ...clearedParams,
    pagination: {
      limit: limit || EMPLOYEES_PAGE_SIZE,
      page,
    },
  };

  const response = await complianceApi.get<APIEmployeeResponse>('employees', {
    params: requestParams,
    ...config,
  });

  return adaptEmployeeResponse(response.data);
}

export function useInfinityGetEmployees(params: GetEmployeesFilterParams) {
  return useInfiniteQuery(
    ['useInfinityGetEmployees', params],
    async ({ pageParam = 1 }) => {
      const response = await searchEmployees({
        ...params,
        limit: EMPLOYEES_PAGE_SIZE,
        page: pageParam,
      });
      return { ...response, pageParam };
    },
    {
      getNextPageParam: lastPage => {
        const nextPage = lastPage.pageParam + 1;
        const totalPages = Math.ceil(lastPage.count / EMPLOYEES_PAGE_SIZE);
        return nextPage <= totalPages ? nextPage : undefined;
      },
      enabled: !!params.employeeNameOrId?.trim().length,
      retry: (failureCount, error: AxiosError) => {
        if (error.response?.status === HttpStatusCode.Unauthorized) {
          return false;
        }
        return failureCount < MAX_RETRY_COUNT;
      },
    }
  );
}
